import {useCallback} from 'react'
import {useDisclosure} from '@salesforce/retail-react-app/app/components/shared/ui'

/**
 * Disclosure state with optional controlled mode and lifecycle callbacks (before open / before close).
 * Chakra {@link useDisclosure} already covers uncontrolled open/close; this adds parent-controlled
 * `open` + `onOpenChange`, plus `onOpening` / `onClosing` for side effects (reset form, discard draft).
 *
 * @param {object} [options]
 * @param {boolean} [options.open] — with `onOpenChange`, disclosure is controlled by the parent
 * @param {(open: boolean) => void} [options.onOpenChange]
 * @param {() => void} [options.onOpening] — runs when transitioning to open (e.g. reset form)
 * @param {() => void} [options.onClosing] — runs when transitioning to closed
 * @returns {{ isOpen: boolean, setOpen: (open: boolean) => void, open: () => void, close: () => void }}
 */
export function useControlledDisclosure({
    open: controlledOpen,
    onOpenChange,
    onOpening,
    onClosing
} = {}) {
    const disclosure = useDisclosure()
    const isControlled = typeof onOpenChange === 'function' && typeof controlledOpen === 'boolean'

    const isOpen = isControlled ? controlledOpen : disclosure.isOpen

    const setOpen = useCallback(
        (next) => {
            if (isControlled) {
                onOpenChange(next)
            } else if (next) {
                disclosure.onOpen()
            } else {
                disclosure.onClose()
            }
        },
        [isControlled, onOpenChange, disclosure]
    )

    const open = useCallback(() => {
        onOpening?.()
        setOpen(true)
    }, [onOpening, setOpen])

    const close = useCallback(() => {
        onClosing?.()
        setOpen(false)
    }, [onClosing, setOpen])

    return {
        isOpen,
        setOpen,
        open,
        close
    }
}
